import clsx from 'clsx';
import { useState } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { ChevronDownIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import { ConfidenceLevel, StrategyRow } from '../StrategyCalculatorPanel.types';
import * as styles from './RecommendationTable.css';

const ConfidenceBadge = ({ level }: { level: ConfidenceLevel }) => (
  <span
    className={clsx(
      styles.confidenceBadge,
      level === 'high' ? styles.confidenceHigh : styles.confidenceMedium
    )}
  >
    {level === 'high' ? (
      <Content id={ContentId.High} />
    ) : (
      <Content id={ContentId.StrategyCalculatorMedium} />
    )}
  </span>
);

export const RecommendationTable = ({
  rows,
  recommendedLabel,
  isMobile,
}: {
  rows: StrategyRow[];
  recommendedLabel: string;
  isMobile: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const colStrategy = isMobile ? styles.colStrategyMobile : styles.colStrategy;
  const colAdCost = isMobile ? styles.colAdCostMobile : styles.colAdCost;
  const colNetRevenue = isMobile
    ? styles.colNetRevenueMobile
    : styles.colNetRevenue;

  return (
    <div className={styles.tableContainer}>
      <div
        className={styles.headerRow}
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsExpanded((prev) => !prev);
          }
        }}
      >
        <span className={styles.headerLabel}>
          <Content id={ContentId.StrategyCalculatorRecommendation} />{' '}
          <span className={styles.headerStrategy}>{recommendedLabel}</span>
        </span>
        <span
          className={clsx(styles.headerChevron, {
            [styles.headerChevronExpanded]: isExpanded,
          })}
        >
          <ChevronDownIcon width={12} height={12} />
        </span>
      </div>

      {isExpanded && (
        <div className={styles.tableBody}>
          <div className={styles.columnHeaderRow}>
            <div className={clsx(styles.columnHeaderCell, colStrategy)}>
              <Content id={ContentId.DefaultBoost_Strategy} />
            </div>
            <div className={clsx(styles.columnHeaderCell, colAdCost)}>
              <Content id={ContentId.StrategyCalculatorAdCost} />
            </div>
            <div className={clsx(styles.columnHeaderCell, colNetRevenue)}>
              <Content id={ContentId.StrategyCalculatorNetRevenue} />
            </div>
            {!isMobile && (
              <div
                className={clsx(styles.columnHeaderCell, styles.colConfidence)}
              >
                <Content id={ContentId.StrategyCalculatorModelConfidence} />
              </div>
            )}
          </div>

          {rows.map((row, index) => {
            const isLast = index === rows.length - 1;

            return (
              <div
                key={row.id}
                className={clsx(styles.dataRow, {
                  [styles.dataRowRecommended]: row.isRecommended,
                  [styles.dataRowLast]: isLast,
                })}
              >
                <div className={clsx(styles.dataCell, colStrategy)}>
                  {row.label}
                  {row.isRecommended && !isMobile && (
                    <>
                      {' ('}
                      <Content id={ContentId.Recommended} />
                      {')'}
                    </>
                  )}
                </div>
                <div className={clsx(styles.dataCell, colAdCost)}>
                  {row.adCost}
                </div>
                <div className={clsx(styles.dataCell, colNetRevenue)}>
                  {row.netRevenue}
                </div>
                {!isMobile && (
                  <div className={clsx(styles.dataCell, styles.colConfidence)}>
                    <ConfidenceBadge level={row.confidence} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
